<template>
  <div class="select-lesson-page">
    <!-- Modern Header -->
    <div class="page-header">
      <div class="header-content">
        <h1>📚 Ders Seçimi</h1>
        <p class="header-subtitle">Randevu almak istediğiniz dersi seçin</p>
      </div>
      <div class="header-actions">
        <button @click="goBack" class="btn-back">
          ← Geri Dön
        </button>
      </div>
    </div>

    <main class="page-content">
      <!-- Instruction Section -->
      <div class="instruction-section">
        <div class="instruction-card">
          <div class="instruction-icon">🎯</div>
          <h2>Randevu için Ders Seçin</h2>
          <p>Randevu almak istediğiniz dersi aşağıdaki listeden seçiniz.</p>
        </div>
      </div>

      <!-- Loading State -->
      <div v-if="isLoading" class="loading-state">
        <div class="loading-spinner"></div>
        <p>Dersler yükleniyor...</p>
      </div>
      
      <!-- Error State -->
      <div v-else-if="error" class="error-state">
        <div class="error-icon">📚</div>
        <h3>Ders Bulunamadı</h3>
        <p>Dersler yüklenirken bir hata oluştu.</p>
        <button @click="goBack" class="btn-back">
          ← Ana Sayfaya Dön
        </button>
      </div>
      
      <!-- Empty State -->
      <div v-else-if="lessons.length === 0" class="empty-state">
        <div class="empty-icon">📚</div>
        <h3>Henüz Ders Bulunmamaktadır</h3>
        <p>Sistemde henüz ders tanımlanmamış. Lütfen daha sonra tekrar deneyin.</p>
      </div>
      
      <!-- Lessons Grid -->
      <div v-else class="lessons-section">
        <div class="section-header">
          <h2>📋 Mevcut Dersler</h2>
          <div class="section-stats">
            <span class="stat-badge">{{ lessons.length }} Ders</span>
          </div>
        </div>
        
        <div class="lessons-grid">
          <div 
            v-for="lesson in lessons" 
            :key="lesson.id" 
            class="lesson-card"
            @click="selectLesson(lesson)"
          >
            <div class="lesson-header">
              <div class="lesson-icon">📖</div>
              <div class="lesson-status">
                <span class="status-badge">Mevcut</span>
              </div>
            </div>
            
            <div class="lesson-content">
              <h3 class="lesson-name">{{ lesson.name }}</h3>
              <div class="lesson-description">
                <p v-if="lesson.description">{{ lesson.description }}</p>
                <p v-else class="no-description">Açıklama bulunmamaktadır.</p>
              </div>
            </div>
            
            <div class="lesson-actions">
              <div class="select-arrow">→</div>
              <span class="select-text">Seç</span>
            </div>
          </div>
        </div>
      </div>
    </main>

    <AppErrorModal :message="errorPopup" @close="closeErrorPopup" />
  </div>
</template>

<script>
import { ref, onMounted, onUnmounted, watch } from 'vue'
import { useStore } from 'vuex'
import { useRouter } from 'vue-router'
import axios from 'axios'
import AppErrorModal from '@/components/AppErrorModal.vue'

export default {
  name: 'SelectLessonView',
  components: { AppErrorModal },
  setup() {
    const store = useStore()
    const router = useRouter()
    
    const lessons = ref([])
    const isLoading = ref(true)
    const error = ref('')

    // Popup hata bildirimi (modal)
    const errorPopup = ref('')
    const pendingRedirect = ref(null)
    const closeErrorPopup = () => {
      errorPopup.value = ''
      if (pendingRedirect.value) {
        const target = pendingRedirect.value
        pendingRedirect.value = null
        router.push(target)
      }
    }

    // 🚨 Güvenlik için sistem durumu kontrolü
    const systemStatus = ref(null)
    const systemStatusInterval = ref(null)

    onMounted(async () => {
      // 🔄 Store'dan kullanıcı bilgisini yeniden yükle
      const token = localStorage.getItem('token')
      if (token) {
        try {
          await store.dispatch('fetchCurrentUser')
        } catch (error) {
          console.error('Kullanıcı bilgisi yüklenemedi:', error)
        }
      }
      
      await fetchLessons()
      
      // 🚨 Güvenlik: Sistem durumunu kontrol et ve sürekli izle
      await checkSystemStatus()
      startSystemStatusMonitoring()
    })
    
    onUnmounted(() => {
      // 🚨 Güvenlik: Interval'ı temizle
      stopSystemStatusMonitoring()
    })

    const fetchLessons = async () => {
      isLoading.value = true
      error.value = ''
      
      try {
        const result = await store.dispatch('fetchLessons')
        
        if (result.success) {
          lessons.value = store.getters.getLessons
        } else {
          error.value = result.error
        }
      } catch (err) {
        error.value = 'Dersler yüklenirken bir hata oluştu'
        console.error(err)
      } finally {
        isLoading.value = false
      }
    }

    const selectLesson = (lesson) => {
      // Nested route kullanarak öğretmen seçim sayfasına git
      router.push({ 
        name: 'StudentSelectTeacher', 
        params: { lessonId: lesson.id },
        query: { lessonName: lesson.name }
      })
    }

    const goBack = () => {
      // Öğrenci paneline geri dön
      router.push('/panel/ogrenci')
    }
    
    // 🚨 Güvenlik: Sistem durumunu kontrol et
    const checkSystemStatus = async () => {
      try {
        const response = await axios.get('https://api.modernkurs.com/api/system/status/user')
        if (response.data.success) {
          systemStatus.value = response.data
          
          // Sistem hazır değilse güvenlik uyarısı ver
          if (!response.data.can_book) {
    
            errorPopup.value = 'Sistem bakım moduna alındı. Ana sayfaya yönlendiriliyorsunuz.'
            pendingRedirect.value = '/panel/ogrenci'
            return false
          }
          
          return true
        } else {
          console.error('❌ Sistem durumu alınamadı:', response.data.message)
          return false
        }
      } catch (error) {
        console.error('❌ Sistem durumu kontrol hatası:', error)
        return false
      }
    }
    
    // 🚨 Güvenlik: Sistem durumunu sürekli izle
    const startSystemStatusMonitoring = () => {
      // Her 10 saniyede bir sistem durumunu kontrol et
      systemStatusInterval.value = setInterval(async () => {
        const isSystemReady = await checkSystemStatus()
        if (!isSystemReady) {
          // Sistem hazır değilse interval'ı durdur
          stopSystemStatusMonitoring()
        }
      }, 10000) // 10 saniye
    }
    
    // 🚨 Güvenlik: Sistem durumu izlemeyi durdur
    const stopSystemStatusMonitoring = () => {
      if (systemStatusInterval.value) {
        clearInterval(systemStatusInterval.value)
        systemStatusInterval.value = null
      }
    }

    return {
      lessons,
      isLoading,
      error,
      errorPopup,
      closeErrorPopup,
      selectLesson,
      goBack,
      // 🚨 Güvenlik fonksiyonları
      systemStatus,
      checkSystemStatus,
      startSystemStatusMonitoring,
      stopSystemStatusMonitoring
    }
  }
}
</script>

<style scoped>
/* Modern Select Lesson Page Styles */
.select-lesson-page {
  min-height: 100vh;
  background: linear-gradient(135deg, #f5f7fa 0%, #c3cfe2 100%);
}

/* Page Header */
.page-header {
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  color: white;
  padding: 2rem;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.1);
  display: flex;
  justify-content: space-between;
  align-items: center;
}

.header-content h1 {
  margin: 0 0 0.5rem 0;
  font-size: 2.5rem;
  font-weight: 700;
}

.header-subtitle {
  margin: 0;
  opacity: 0.9;
  font-size: 1.1rem;
}

.header-actions {
  display: flex;
  align-items: center;
}

.btn-back {
  background: rgba(255, 255, 255, 0.1);
  color: white;
  border: 2px solid rgba(255, 255, 255, 0.2);
  padding: 0.75rem 1.5rem;
  border-radius: 25px;
  font-size: 1rem;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.3s;
  display: flex;
  align-items: center;
  gap: 0.5rem;
}

.btn-back:hover {
  background: rgba(255, 255, 255, 0.2);
  border-color: rgba(255, 255, 255, 0.3);
  transform: translateY(-2px);
}

/* Page Content */
.page-content {
  padding: 2rem;
  max-width: 1400px;
  margin: 0 auto;
}

/* Instruction Section */
.instruction-section {
  margin-bottom: 2rem;
}

.instruction-card {
  background: white;
  border-radius: 25px;
  padding: 2rem;
  text-align: center;
  box-shadow: 0 8px 30px rgba(0, 0, 0, 0.1);
  border: 1px solid #e9ecef;
  position: relative;
  overflow: hidden;
}

.instruction-card::before {
  content: '';
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  height: 4px;
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
}

.instruction-icon {
  font-size: 3.5rem;
  margin-bottom: 1rem;
}

.instruction-card h2 {
  margin: 0 0 0.75rem 0;
  font-size: 1.8rem;
  color: #333;
  font-weight: 700;
}

.instruction-card p {
  margin: 0;
  color: #6c757d;
  font-size: 1.1rem;
  line-height: 1.5;
}

/* Loading State */
.loading-state {
  text-align: center;
  padding: 3rem;
  color: #6c757d;
}

.loading-spinner {
  width: 40px;
  height: 40px;
  border: 4px solid #f3f3f3;
  border-top: 4px solid #4CAF50;
  border-radius: 50%;
  animation: spin 1s linear infinite;
  margin: 0 auto 1rem;
}

@keyframes spin {
  0% { transform: rotate(0deg); }
  100% { transform: rotate(360deg); }
}

/* Error State */
.error-state {
  text-align: center;
  padding: 3rem;
  color: #6c757d;
  background: white;
  border-radius: 25px;
  box-shadow: 0 8px 30px rgba(0, 0, 0, 0.1);
  border: 1px solid #e9ecef;
}

.error-icon {
  font-size: 4rem;
  margin-bottom: 1rem;
}

.error-state h3 {
  margin: 0 0 1rem 0;
  color: #333;
  font-size: 1.5rem;
}

.error-state p {
  margin: 0 0 1.5rem 0;
  color: #6c757d;
}

.btn-retry {
  background: linear-gradient(135deg, #4CAF50 0%, #45a049 100%);
  color: white;
  border: none;
  padding: 1rem 2rem;
  border-radius: 25px;
  font-size: 1rem;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.3s;
  box-shadow: 0 4px 15px rgba(76, 175, 80, 0.3);
}

.btn-retry:hover {
  transform: translateY(-2px);
  box-shadow: 0 6px 20px rgba(76, 175, 80, 0.4);
}

/* Empty State */
.empty-state {
  text-align: center;
  padding: 3rem;
  color: #6c757d;
  background: white;
  border-radius: 25px;
  box-shadow: 0 8px 30px rgba(0, 0, 0, 0.1);
  border: 1px solid #e9ecef;
}

.empty-icon {
  font-size: 4rem;
  margin-bottom: 1rem;
}

.empty-state h3 {
  margin: 0 0 1rem 0;
  color: #333;
  font-size: 1.5rem;
}

.empty-state p {
  margin: 0;
  color: #6c757d;
}

/* Lessons Section */
.lessons-section {
  margin-bottom: 2rem;
}

.section-header {
  margin-bottom: 2rem;
  text-align: center;
}

.section-header h2 {
  margin: 0 0 0.5rem 0;
  font-size: 2rem;
  color: #333;
  font-weight: 700;
}

.section-stats {
  display: flex;
  justify-content: center;
  margin-top: 1rem;
}

.stat-badge {
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  color: white;
  padding: 0.5rem 1rem;
  border-radius: 20px;
  font-size: 0.9rem;
  font-weight: 600;
}

/* Lessons Grid */
.lessons-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(400px, 1fr));
  gap: 2rem;
}

.lesson-card {
  background: white;
  border-radius: 20px;
  padding: 2rem;
  box-shadow: 0 8px 30px rgba(0, 0, 0, 0.1);
  border: 1px solid #e9ecef;
  cursor: pointer;
  transition: all 0.3s;
  position: relative;
  overflow: hidden;
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  min-height: 200px;
}

.lesson-card::before {
  content: '';
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  height: 3px;
  background: linear-gradient(135deg, #4CAF50 0%, #45a049 100%);
}

.lesson-card:hover {
  transform: translateY(-8px);
  box-shadow: 0 15px 40px rgba(0, 0, 0, 0.15);
}

.lesson-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 1.5rem;
}

.lesson-icon {
  font-size: 2.5rem;
}

.lesson-status {
  display: flex;
  gap: 0.5rem;
}

.status-badge {
  background: linear-gradient(135deg, #28a745 0%, #20c997 100%);
  color: white;
  padding: 0.25rem 0.75rem;
  border-radius: 12px;
  font-size: 0.8rem;
  font-weight: 600;
  text-transform: uppercase;
}

.lesson-content {
  flex: 1;
  margin-bottom: 1.5rem;
}

.lesson-name {
  margin: 0 0 1rem 0;
  font-size: 1.5rem;
  color: #333;
  font-weight: 600;
}

.lesson-description {
  color: #6c757d;
  line-height: 1.6;
}

.lesson-description p {
  margin: 0;
}

.no-description {
  color: #999;
  font-style: italic;
}

.lesson-actions {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding-top: 1rem;
  border-top: 1px solid #f1f3f4;
}

.select-arrow {
  font-size: 1.5rem;
  color: #4CAF50;
  transition: transform 0.3s;
}

.select-text {
  color: #4CAF50;
  font-weight: 600;
  font-size: 0.9rem;
}

.lesson-card:hover .select-arrow {
  transform: translateX(5px);
}

.lesson-card:hover .select-text {
  color: #45a049;
}

/* Responsive tasarım */
@media (max-width: 768px) {
  .page-header {
    padding: 1.5rem 1rem;
    flex-direction: column;
    gap: 1rem;
    text-align: center;
  }
  
  .header-content h1 {
    font-size: 2rem;
  }
  
  .page-content {
    padding: 1rem;
  }
  
  .instruction-card {
    padding: 2rem 1.5rem;
  }
  
  .instruction-card h2 {
    font-size: 1.5rem;
  }
  
  .lessons-grid {
    grid-template-columns: 1fr;
    gap: 1.5rem;
  }
  
  .lesson-card {
    padding: 1.5rem;
    min-height: 180px;
  }
}

@media (max-width: 480px) {
  .page-header {
    padding: 1rem 0.75rem;
    gap: 0.75rem;
  }
  
  .header-content h1 {
    font-size: 1.5rem;
  }
  
  .header-subtitle {
    font-size: 1rem;
  }
  
  .instruction-card {
    padding: 1.5rem 1rem;
  }
  
  .instruction-card h2 {
    font-size: 1.3rem;
  }
  
  .instruction-card p {
    font-size: 1rem;
  }
  
  .lesson-card {
    padding: 1rem;
    min-height: 160px;
  }
  
  .lesson-name {
    font-size: 1.3rem;
  }
}
</style> 